import type { EyeStyleOptions, EyeType } from '../../AppearanceDefinitions';
import type { HumanoidPose } from '../AttachmentGeometry';

/**
 * `matched` eye style (plan 12 §7, DJ 2026-10-07): the player's own eye
 * language — white eye, dark pupil, catch-light, and every `eyes` expression —
 * drawn as two EQUAL eyes, both the size of the old near eye. Facing is carried
 * by placement instead of size: the pair sits a few units toward the facing
 * side, both pupils look forward, and FaceParts shifts the mouth the same way.
 * The old near-big/far-small look stays available as the opt-in `profile`.
 *
 * Coordinates are the right-facing face space used by FaceParts; the caller
 * keeps the face-rig offset and the whole-sprite mirror for left facing.
 */

const PUPIL = '#26313e';

/** Units the eyes and mouth move toward the facing side (the head's centre is x≈62). */
export const MATCHED_FACING_SHIFT = 3;

export interface MatchedSocket { x: number; y: number; rx: number; ry: number }

const round2 = (v: number) => Number(v.toFixed(2));
const clamp = (v: number | undefined, lo: number, hi: number, fallback: number) =>
  (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v as number)) : fallback);

/** The equal socket pair; knobs clamp like the rest of the eye library. */
export function matchedSockets(options: Omit<EyeStyleOptions, 'style'> = {}): { far: MatchedSocket; near: MatchedSocket } {
  const k = clamp(options.scale, 0.7, 1.4, 1);
  const spread = clamp(options.spacing, -6, 6, 0) / 2;
  const y = 43 + clamp(options.height, -5, 5, 0);
  const rx = round2(6.2 * k);
  const ry = round2(7 * k);
  return {
    far: { x: round2(49 + MATCHED_FACING_SHIFT - spread), y, rx, ry },
    near: { x: round2(75 + MATCHED_FACING_SHIFT + spread), y, rx, ry },
  };
}

type Side = 'far' | 'near';

/** Equal dark sockets retain the art NPC finish, including both catch-lights. */
function ghostEye(side: Side, e: MatchedSocket, ink: string, gazeX: number, gazeY: number, sleepy: boolean): string {
  const x = round2(e.x + gazeX * 0.6), y = round2(e.y + gazeY * 0.6);
  const ry = round2(e.ry * (sleepy ? 3.8 : 7.8) / 7);
  const glint = round2(e.rx * 2.2 / 6.2);
  const rim = ink === PUPIL || ink === '#303540' ? '' : ` stroke="${ink}" stroke-width="1"`;
  return `<g data-face-eye="${side}" data-face-ghost-eye="${side}">
    <ellipse data-face-socket="${side}" cx="${x}" cy="${y}" rx="${e.rx}" ry="${ry}" fill="${PUPIL}"${rim} />
    <circle cx="${round2(x + e.rx * 0.32)}" cy="${round2(y - ry * 0.38)}" r="${glint}" fill="#ffffff" />
    <circle cx="${round2(x - e.rx * 0.35)}" cy="${round2(y + ry * 0.35)}" r="${round2(glint * 0.45)}" fill="#ffffff" opacity="0.8" />
  </g>`;
}

function openEye(side: Side, e: MatchedSocket, ink: string, gazeX: number, gazeY: number, sleepy: boolean): string {
  const ry = sleepy ? round2(e.ry * 0.45) : e.ry;
  const radius = round2(e.rx * (sleepy ? 0.31 : 0.48));
  const pupilX = round2(e.x + e.rx * 0.24 + gazeX);
  const pupilY = round2(e.y + gazeY);
  return `<g data-face-eye="${side}">
    <ellipse data-face-socket="${side}" cx="${e.x}" cy="${e.y}" rx="${e.rx}" ry="${ry}" fill="#fff9eb" stroke="${ink}" stroke-width="1.8" />
    <circle data-face-pupil="${side}" cx="${pupilX}" cy="${pupilY}" r="${radius}" fill="${PUPIL}" />
    <circle cx="${round2(pupilX + 0.7)}" cy="${round2(pupilY - 0.9)}" r="${round2(e.rx * 0.145)}" fill="#ffffff" />
  </g>`;
}

const arc = {
  closed: (e: MatchedSocket) => `M${round2(e.x - e.rx - 1)},${e.y} Q${e.x},${round2(e.y + 5)} ${round2(e.x + e.rx + 1)},${e.y}`,
  happy: (e: MatchedSocket) => `M${round2(e.x - e.rx - 1)},${round2(e.y + 1)} Q${e.x},${round2(e.y - 9)} ${round2(e.x + e.rx + 1)},${round2(e.y + 1)}`,
  hurt: (e: MatchedSocket, side: Side) => {
    const a = side === 'near' ? e.x + e.rx : e.x - e.rx;
    const b = side === 'near' ? e.x - e.rx + 1 : e.x + e.rx - 1;
    return `M${round2(a)},${round2(e.y - 5)} L${round2(b)},${e.y} L${round2(a)},${round2(e.y + 4)}`;
  },
  cross: (e: MatchedSocket) => {
    const d = e.rx * 0.8;
    return `M${round2(e.x - d)},${round2(e.y - d)} L${round2(e.x + d)},${round2(e.y + d)} M${round2(e.x + d)},${round2(e.y - d)} L${round2(e.x - d)},${round2(e.y + d)}`;
  },
  /** Brow above an eye; `drop` lowers the inner end (angry/focus), `lift` raises the whole brow. */
  brow: (e: MatchedSocket, side: Side, lift: number, drop: number) => {
    const outer = side === 'far' ? e.x - e.rx - 1 : e.x + e.rx + 1;
    const inner = side === 'far' ? e.x + e.rx : e.x - e.rx;
    const top = e.y - e.ry - lift;
    return `M${round2(outer)},${round2(top)} L${round2(inner)},${round2(top + drop)}`;
  },
  lid: (e: MatchedSocket) => `M${round2(e.x - e.rx - 0.5)},${round2(e.y - e.ry * 0.45)} L${round2(e.x + e.rx + 0.5)},${round2(e.y - e.ry * 0.45)}`,
  star: (e: MatchedSocket) => {
    const pts: string[] = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? e.ry * 1.05 : e.ry * 0.45;
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      pts.push(`${round2(e.x + r * Math.cos(a))},${round2(e.y + r * Math.sin(a))}`);
    }
    return `M${pts.join(' L')} Z`;
  },
};

/** Two matching eyes in the `eyes` expression; poses override like the legacy renderer. */
export function renderMatchedEyes(style: EyeType, ink: string, pose: HumanoidPose, options: Omit<EyeStyleOptions, 'style'> = {}, ghost = false): string {
  const { far, near } = matchedSockets(options);
  const state = pose === 'death' || pose === 'blink' || pose === 'hurt' || pose === 'attack' || pose === 'cast' ? pose : 'open';
  const gazeX = pose === 'attack' ? 0.8 : pose === 'cast' ? 0.55 : 0;
  const gazeY = pose === 'cast' ? -0.5 : pose === 'attack' ? -0.2 : 0;
  const stroke = `fill="none" stroke="${ink}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"`;
  const open = (side: Side, sleepy = false) => (ghost ? ghostEye : openEye)(side, side === 'far' ? far : near, ink, gazeX, sleepy ? 0 : gazeY, sleepy);
  let content: string;
  if (pose === 'death') content = `<path ${stroke} d="${arc.cross(far)} ${arc.cross(near)}" />`;
  else if (pose === 'blink') content = `<path ${stroke} d="${arc.closed(far)} ${arc.closed(near)}" />`;
  else if (pose === 'hurt') content = `<path ${stroke} d="${arc.hurt(far, 'far')} ${arc.hurt(near, 'near')}" />`;
  else {
    switch (style) {
    case 'happy': content = `<path ${stroke} d="${arc.happy(far)} ${arc.happy(near)}" />`; break;
    case 'winking': content = open('far') + `<path ${stroke} d="${arc.happy(near)}" />`; break;
    case 'stars':
      content = `<g data-face-star-eyes="true" fill="#ffd36a" stroke="${ink}" stroke-width="1.1" stroke-linejoin="round" transform="translate(${gazeX} ${gazeY})"><path d="${arc.star(far)}" /><path d="${arc.star(near)}" /></g>`;
      break;
    case 'sleepy': content = open('far', true) + open('near', true) + (ghost ? '' : `<path ${stroke} d="${arc.lid(far)} ${arc.lid(near)}" />`); break;
    case 'angry': content = open('far') + open('near') + `<path ${stroke} d="${arc.brow(far, 'far', 2, 4)} ${arc.brow(near, 'near', 2, 4)}" />`; break;
    case 'round':
    default: content = open('far') + open('near'); break;
    }
    if (pose === 'attack' && (style === 'round' || style === 'winking')) {
      content += `<path data-face-focus="attack" ${stroke} d="${arc.brow(far, 'far', 1, 2)} ${arc.brow(near, 'near', 1, 2)}" />`;
    } else if (pose === 'cast' && style === 'round') {
      content += `<path data-face-focus="cast" ${stroke} d="${arc.brow(far, 'far', 3, 0)} ${arc.brow(near, 'near', 3, 0)}" />`;
    }
  }
  return `<g data-face-style="${style}" data-face-state="${state}" data-face-gaze="right" data-face-eye-library="matched">${content}</g>`;
}
